import {
  // shared
  LayoutDashboard,
  Database,
  GitBranch,
  Code2,
  Cable,
  Search,
  Sparkles,
  Workflow,
  ShieldCheck,
  Layers,
  LineChart,
  // research & analysis
  DownloadCloud,
  GitMerge,
  Star,
  Scale,
  Trophy,
  Target,
  TrendingUp,
  MapPin,
  Globe2,
  // communication & storytelling
  BarChart3,
  PenTool,
  BookOpen,
  Crosshair,
  MessageSquare,
  Presentation,
  Lightbulb,
  Rss,
  // distribution & reach
  Share2,
  Mail,
  Megaphone,
  Share,
  MousePointerClick,
  BellRing,
  Telescope,
  Newspaper,
  // fresh data & updates
  RefreshCw,
  CalendarClock,
  Eye,
  FileText,
  Bell,
  GitCompare,
} from "lucide-react";

// Rich, per-service content. Keyed by `slug` (matching SERVICES in WhatWeDo.jsx).
// Any service without an entry here falls back to the generic "Coming soon" view.
// All fields except `hero` and `intro` + `capabilities` + `cta` are OPTIONAL.
export const SERVICE_CONTENT = {
  // ────────────────────────────────────────────────────────────────
  // 01 — Data Products & Engineering
  // ────────────────────────────────────────────────────────────────
  "data-products-engineering": {
    hero: {
      tagline: "Build useful tools from data.",
    },
    intro: {
      lead: "We build and maintain databases, pipelines, and digital tools.",
      paragraphs: [
        "These tools are designed to make your data easier to use.",
        "They might be a dashboard for your team, a tracker for customers, a search tool, or an automated feed that keeps information current.",
        "We also connect to your existing systems, so your tools can be built without starting from scratch.",
      ],
    },
    capabilities: {
      title: "What we build",
      kicker: "Toolbox",
      sub: "Seven highlights from our products and engineering toolbox. These are the key building blocks we use to turn data into trustworthy tools people can use.",
      items: [
        { name: "Digital products", desc: "End-to-end products for teams and audiences. Includes self-serve dashboards, trackers, and interactive games.", icon: Layers },
        { name: "Databases", desc: "Structured data stores built around how you work and what you need to find.", icon: Database },
        { name: "Feeds, Pipelines & APIs", desc: "Systems that collect data, clean it, update it and deliver it where it needs to go. APIs that let your data plug into other products and partners.", icon: GitBranch },
        { name: "Legacy integrations", desc: "Connect to your CMS, spreadsheets and in-house tools without rebuilding everything.", icon: Cable },
        { name: "Discovery layers", desc: "AI tools for chat and assisted search built on your trusted data.", icon: Sparkles },
        { name: "Automation", desc: "Scheduled jobs, alerts and workflows that quietly handle repetitive work.", icon: Workflow },
        { name: "Data integrity", desc: "Clear sources, definitions and review steps that keep information reliable.", icon: ShieldCheck },
      ],
    },
    caseStudy: {
      eyebrow: "Case study — The Energy",
      title: "An automated dashboard\nbehind a daily newsroom.",
      body:
        "The Energy is a media startup with a widely read daily newsletter. We built an automated dashboard that tracks their key topics and lets them add trusted data and visualisations to their content on demand.",
      tags: ["Newsroom tool", "Automated dashboard", "Trusted data on demand"],
    },
    testimonial: {
      quote:
        "Working with OnlyFacts helps our small team save hours each week so we can focus on our journalism. They're responsive to our needs and the dashboard they built us has helped streamline our weekly data newsletter.",
      name: "Charis Palmer",
      org: "The Energy",
      initials: "CP",
    },
    cta: {
      title: "Got data that should be doing more?",
      sub: "Every project starts with a conversation. Tell us what you have and what you wish it could do.",
      primary: "Start a project",
      secondary: "See our services",
    },
  },

  // ────────────────────────────────────────────────────────────────
  // 02 — Research & Analysis
  // ────────────────────────────────────────────────────────────────
  "research-analysis": {
    hero: {
      tagline: "Find the figures that matter.",
    },
    intro: {
      lead: "Find, combine, and interpret the figures that matter.",
      paragraphs: [
        "Sound decision-making and strong communication starts with good evidence.",
        "We combine journalistic chops with academic rigour to find relevant data, merge fragmented sources, test claims, and identify the numbers worth paying attention to.",
      ],
    },
    capabilities: {
      title: "How we dig in",
      kicker: "Toolbox",
      sub: "Nine techniques we mix and match to move from raw numbers to a defensible evidence base.",
      items: [
        { name: "Sourcing data", desc: "Tracking down the right public, private and proprietary sources for the question at hand.", icon: DownloadCloud },
        { name: "Cleaning", desc: "Reshaping messy inputs into a trustworthy, analysis-ready dataset.", icon: Sparkles },
        { name: "Verifying claims", desc: "Stress-testing figures and statements before they go near a published case.", icon: ShieldCheck },
        { name: "Combining sources", desc: "Joining fragmented datasets into a single, coherent view of the issue.", icon: GitMerge },
        { name: "Headline numbers", desc: "Identifying the one or two figures worth leading with — and why.", icon: Star },
        { name: "Comparisons & benchmarks", desc: "Peer, sector and historical framing that makes the number matter.", icon: Scale },
        { name: "Trends & trajectories", desc: "Momentum, current path and consequences if nothing changes.", icon: TrendingUp },
        { name: "Driver breakdowns", desc: "Unpacking the causes behind a headline number — the anatomy of the metric.", icon: GitBranch },
        { name: "Segmented views", desc: "Cuts by market, cohort, geography or channel where the story really lives.", icon: MapPin },
      ],
    },
    // Case study / testimonial intentionally omitted — PSG content pending.
    cta: {
      title: "Need the evidence behind a decision?",
      sub: "Tell us what you're trying to understand. We'll help build the evidence base.",
      primary: "Start a project",
      secondary: "See our services",
    },
  },

  // ────────────────────────────────────────────────────────────────
  // 03 — Data Storytelling
  // ────────────────────────────────────────────────────────────────
  "communication-storytelling": {
    hero: {
      tagline: "Turn evidence into stories people understand and remember.",
    },
    intro: {
      lead: "Don't just build a chart or dashboard. Build a case.",
      paragraphs: [
        "Data struggles to cut through in a crowded attention economy.",
        "Charts often need time or expertise to interpret. Dashboards invite multiple readings and dilute signals. Infographics get oversimplified and fail to earn trust.",
        "We help make your case clear and compelling."
      ],
    },
    capabilities: {
      title: "How we tell the story",
      kicker: "Toolbox",
      sub: "Ten techniques for turning the evidence into something people understand, remember, share, and act on.",
      items: [
        { name: "Visualisations", desc: "Tables and charts — downloadable, embeddable and built for the story you're telling.", icon: BarChart3 },
        { name: "Hero charts", desc: "High-impact, single-message visuals that carry the headline argument alone.", icon: TrendingUp },
        { name: "Clear writing & annotations", desc: "Clear, scannable writing. On-chart and in-line callouts that point the reader to what's important.", icon: PenTool },
        { name: "Related articles", desc: "Explainers, methodology notes, and data dictionaries that make complex work legible.", icon: BookOpen },
        { name: "Bigger-picture context", desc: "Global, local and economic framing that situates the data in a world people recognise.", icon: Globe2 },
        { name: "Localising data", desc: "National issues shown through local figures people recognise.", icon: MapPin },
        { name: "Rankings & scorecards", desc: "Structured leaderboards that make relative performance legible.", icon: Trophy },
        { name: "Progress against targets", desc: "Distance travelled and distance remaining — held to an explicit line.", icon: Target },
        { name: "Useful comparisons", desc: "Concrete numbers and comparisons that make an abstract point impossible to ignore.", icon: Lightbulb },
        { name: "Live-updating articles", desc: "Long-form pieces with embedded live charts that stay accurate as the data moves.", icon: Rss },
      ],
    },
    caseStudy: {
      eyebrow: "Case study — Chris O'Brien Lifehouse",
      title: "Patient outcomes,\ntold with balance\nand heart.",
      body:
        "Chris O'Brien Lifehouse needed to demonstrate superior patient outcomes from its model of care. We produced major reports that presented the evidence clearly and told the story with balance and heart.",
      tags: ["Patient outcomes", "Major reports", "Clinical storytelling"],
    },
    testimonial: {
      quote:
        "The team turned our data about patient outcomes into powerful messages that told a clear story to lay audiences, despite the complex and nuanced subject matter.",
      name: "Shirin Amirbeaggi",
      org: "Director of Marketing & Stakeholder Engagement, Chris O'Brien Lifehouse",
      initials: "SA",
    },
    cta: {
      title: "Have a case to make?",
      sub: "Tell us what needs to land. We'll help shape the case.",
      primary: "Start a project",
      secondary: "See our services",
    },
  },

  // ────────────────────────────────────────────────────────────────
  // 04 — Publishing & Reach
  // ────────────────────────────────────────────────────────────────
  "distribution-reach": {
    hero: {
      tagline: "Put evidence in front of people.",
    },
    intro: {
      lead: "Data-led content and tools designed for distribution.",
      paragraphs: [
        "Audiences are fragmented, and increasingly filtered through algorithms, search engines, and AI assistants.",
        "We help you meet people where they are across AI, email, social, search, and media channels.",
      ],
    },
    capabilities: {
      title: "How we get it out there",
      kicker: "Toolbox",
      sub: "Seven tools we use to put evidence in front of people. All drawing on the same trusted source.",
      items: [
        { name: "Shareable, embeddable assets", desc: "Charts, cards and widgets built to be lifted into partner sites, media and decks.", icon: Share2 },
        { name: "Digital publishing", desc: "Articles, explainers and live data pages or feeds built for your own platform.", icon: Share2 },
        { name: "Email newsletters & campaigns", desc: "Owned-channel editorial and targeted, sends for supporters, members and stakeholders.", icon: Mail },
        { name: "Search & Answer Engines", desc: "Long-form articles structured for both traditional search and the answer engines AI relies on.", icon: Telescope },
        { name: "Organic social media", desc: "Platform-native posts, carousels and snippets designed for each feed.", icon: Share },
        { name: "Mobile push alerts", desc: "Real-time notifications tied to updates that actually warrant an interruption.", icon: BellRing },
        { name: "Syndication", desc: "Partnerships and re-publishing deals that put your evidence in front of someone else's audience.", icon: Newspaper },
      ],
    },
    caseStudy: {
      eyebrow: "Case study — The a2 Milk Company",
      title: "Complex science,\nin accessible language.",
      body:
        "The a2 Milk Company needed to explain the scientific research underpinning the A2-type protein in cow's milk. We produced evidence-based articles that translated complex research into domain authority.",
      tags: ["Science translation", "Evidence-based content", "Domain authority"],
    },
    testimonial: {
      quote:
        "The OnlyFacts team brought huge credibility to our content, and were critical in our strategy to create rich digital content that raised our domain authority. Their combination of skills is unique.",
      name: "Kate Haddad",
      org: "Digital Manager, The a2 Milk Company",
      initials: "KH",
    },
    cta: {
      title: "Need your evidence seen?",
      sub: "Good evidence deserves an audience. We'll help it get there.",
      primary: "Start a project",
      secondary: "See our services",
    },
  },

  // ────────────────────────────────────────────────────────────────
  // 05 — Fresh Data & Updates
  // ────────────────────────────────────────────────────────────────
  "fresh-data-updates": {
    hero: {
      tagline: "Keep information relevant and noisy.",
    },
    intro: {
      lead: "Always-current figures. Turn new data into new content.",
      paragraphs: [
        "Data updates help keep attention over time. They shine a relentless spotlight on an issue, build momentum, and create fresh reasons to return and engage.",
        "Our team manages regular automated or manual data updates and alerts, live feeds, and ongoing monitoring and summaries of what changed.",
      ],
    },
    capabilities: {
      title: "How we keep up the volume",
      kicker: "Toolbox",
      sub: "Eight capabilities that turn a one-off launch into a 'drumbeat' of data updates that demand attention over time.",
      items: [
        { name: "Update pipelines", desc: "Reliable flows that pick up new data and push it through your platform end-to-end.", icon: Workflow },
        { name: "Automated refreshes", desc: "Hands-off jobs that re-run on a schedule and quietly keep the numbers current.", icon: RefreshCw },
        { name: "Scheduled updates", desc: "Editorial cadence — daily, weekly, monthly — designed around when the story matters.", icon: CalendarClock },
        { name: "Change detection", desc: "Rules that watch for movement in the data and flag the moments worth covering.", icon: Eye },
        { name: "Update summaries", desc: "Human-readable digests of what's new, ready to drop into a newsletter or post.", icon: FileText },
        { name: "Refreshed figures & charts", desc: "Every visual regenerated against the latest numbers, no manual redraw needed.", icon: BarChart3 },
        { name: "Change alerts", desc: "Email, Slack and push notifications the moment something important shifts.", icon: Bell },
        { name: "Changelog of what moved", desc: "A clear diff between this update and the last — so readers see exactly what changed.", icon: GitCompare },
      ],
    },
    // Case study / testimonial intentionally omitted — content pending.
    cta: {
      title: "Want your data to stay current and noisy?",
      sub: "If the numbers change, the story should too. We'll help make that happen.",
      primary: "Start a project",
      secondary: "See our services",
    },
  },
};
